import {
  useTextureDistribution,
  useTextureStats,
} from '@src/modules/performance/context/reports-api-hooks';
import { Text, Wrap, VStack } from '@chakra-ui/react';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ToggleableBarChart from '@src/modules/performance/components/charts/ToggleableBarChart';
import StatCard from '@src/modules/performance/components/charts/StatCard';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'CCDC Texture',
  content: [
    {
      title: null,
      description:
  <Text fontSize="inherit" mb={2}>
    Road surface macrotexture is a measure of the larger gaps and irregularities between the aggregate particles on the road surface.
  </Text>,
      source: null,
    },
    {
      title: '% Chipseal Texture > 1.0mm',
      description:
  <Text fontSize="inherit" mb={2}>
    The percentage of the chipseal network that has texture above 1mm.
  </Text>,
      source: null,
    },
    {
      title: 'Annual distribution of chipseal texture ',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      The chart shows the annual comparison of the percentage of the surveyed network that is in each chipseal texture band. The bands are: &gt;= 1.8 (very good), &gt;= 1.4 and &lt; 1.8 (good), &gt;= 1.0 and &lt; 1.4 (average), &gt;= 0.7 and &lt; 1.0 (poor), &lt; 0.7 (very poor).
    </Text>
    <Text fontSize="inherit" mb={2}>
      The percentages are based on the latest CCDC data recorded against each 10m road section in the network at the end of each financial year.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The texture results are taken from the mean values of the left wheel position and right wheel position, for each 10m length of road surveyed.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The results are calculated for the network length covered by CCDC surveys.
    </Text>
  </>,
      source: null,
    },
    {
      title: '% Asphalt Texture > 0.7mm',
      description:
  <Text fontSize="inherit" mb={2}>
    The percentage of the asphalt network that has texture above 0.7mm.
  </Text>,
      source: null,
    },
    {
      title: 'Annual distribution of asphalt texture ',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      The chart shows the annual comparison of the percentage of the CCDC surveyed network that is in each asphalt texture band. The bands are: &gt;= 1.3 (very good), &gt;= 1.0 and &lt; 1.3 (good), &gt;= 0.7 and &lt; 1.0 (average), &gt;= 0.5 and &lt; 0.7 (poor), &lt; 0.5 (very poor).
      {' '}
    </Text>
    <Text fontSize="inherit" mb={2}>
      The percentages are based on the latest CCDC data recorded against each 10m road section in the network at the end of each financial year.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The texture results are taken from the mean values of the left wheel position and right wheel position, for each 10m length of road surveyed.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The results are calculated for the network length covered by CCDC surveys.
    </Text>
    <Text fontSize="inherit" mb={2}>
      There is no CCDC data available for State Highways.
    </Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from the Automated High-Speed Data collated through the CCDC programme
  </Text>,
    },
  ],
};

const CHIPSEAL_CHART_CATEGORIES = [
  { label: '>= 1.8', key: 'textureBin1Perc' },
  { label: '>=1.4 & <1.8', key: 'textureBin2Perc' },
  { label: '>=1.0 & <1.4', key: 'textureBin3Perc' },
  { label: '>=0.7 & <1.0', key: 'textureBin4Perc' },
  { label: '<0.7', key: 'textureBin5Perc' },
];

const ASPHALT_CHART_CATEGORIES = [
  { label: '>= 1.3', key: 'textureBin1Perc' },
  { label: '>=1.0 & <1.3', key: 'textureBin2Perc' },
  { label: '>=0.7 & <1.0', key: 'textureBin3Perc' },
  { label: '>=0.5 & <0.7', key: 'textureBin4Perc' },
  { label: '<0.5', key: 'textureBin5Perc' },
];

function RoadConditionTexture() {
  const { data: textureStats, isLoading: isLoadingTextureStats } = useTextureStats();
  const { data: textureDistribution, isLoading: isLoadingTextureDistribution } = useTextureDistribution();
  const isLoading = isLoadingTextureStats || isLoadingTextureDistribution;

  const amDistribtion = textureDistribution?.filter((item) => item.surfaceType === 'AM');
  const csDistribtion = textureDistribution?.filter((item) => item.surfaceType === 'CS');

  const amStats = textureStats?.find((item) => item.surfaceType === 'AM');
  const csStats = textureStats?.find((item) => item.surfaceType === 'CS');

  return (
    <ReportSection
      title="CCDC Texture"
      isLoading={isLoading || !textureStats || !textureDistribution}
      lockYear={textureStats?.quadLockYear}
      informationSource={informationSource}
    >
      <Wrap spacing={6} justify="center" width="100%">
        <VStack spacing={6} align="flex-start">
          <StatCard
            value={csStats?.textureTargetPerc}
            label="Chipseal Texture > 1.0mm"
            suffix="%"
          />
          <ToggleableBarChart
            data={csDistribtion || []}
            categories={CHIPSEAL_CHART_CATEGORIES}
            seriesKey="quadLockYear"
            chartTitle="Annual distribution of chipseal texture"
            yAxisLabel="Percentage"
            colors={[
              CHART_COLOURS.blue,
              CHART_COLOURS.red,
              CHART_COLOURS.green,
              CHART_COLOURS.purple,
              CHART_COLOURS.orange,
              CHART_COLOURS.yellow,
              CHART_COLOURS.magenta,
            ]}
          />
        </VStack>
        <VStack spacing={6} align="flex-start">
          <StatCard
            value={amStats?.textureTargetPerc}
            label="Asphalt Texture > 0.7mm"
            suffix="%"
          />
          <ToggleableBarChart
            data={amDistribtion || []}
            categories={ASPHALT_CHART_CATEGORIES}
            seriesKey="quadLockYear"
            chartTitle="Annual distribution of asphalt texture"
            yAxisLabel="Percentage"
            colors={[
              CHART_COLOURS.blue,
              CHART_COLOURS.red,
              CHART_COLOURS.green,
              CHART_COLOURS.purple,
              CHART_COLOURS.orange,
              CHART_COLOURS.yellow,
              CHART_COLOURS.magenta,
            ]}
          />
        </VStack>
      </Wrap>
    </ReportSection>
  );
}

export default RoadConditionTexture;
